import { useEffect, useState } from "react";
import { X, Smartphone } from "lucide-react";
import { AddToDeviceDialog } from "@/components/common/AddToDeviceDialog";

const SEEN_KEY = "aos-portal-a2h-seen-v1";
const DELAY_MS = 3500;

/**
 * Promemoria discreto (bottom sheet non modale) mostrato la prima volta che un
 * utente apre il Portale Cliente da mobile. Non blocca la navigazione: si può
 * chiudere con X o cliccare "Aggiungi" per aprire il dialog completo. Una volta
 * gestito, non riappare (flag in localStorage `aos-portal-a2h-seen-v1`).
 */
export function BookmarkAppPrompt({ platformName = "Aesthetic OS", clientName }) {
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    // Solo mobile, solo prima volta, non se già installato come standalone (PWA).
    try {
      if (localStorage.getItem(SEEN_KEY) === "1") return;
    } catch { /* storage disabled → mostra comunque una volta */ }
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator?.standalone === true;
    if (isStandalone) {
      try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
      return;
    }
    const ua = (navigator.userAgent || "").toLowerCase();
    const isMobileUA = /iphone|ipad|ipod|android|mobile/.test(ua);
    // Fallback per WebView/Playwright/desktop resize: viewport stretto + touch.
    const isNarrow = typeof window !== "undefined" && window.innerWidth <= 820;
    const hasTouch = typeof navigator !== "undefined" && (navigator.maxTouchPoints || 0) > 0;
    if (!isMobileUA && !(isNarrow && hasTouch) && !isNarrow) return;

    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
  };

  const openDialog = () => {
    setDialogOpen(true);
    // Non consideriamo l'apertura del dialog come "gestita" finché l'utente
    // non lo chiude o non aggiunge davvero: aspettiamo il close del dialog.
  };

  const onDialogChange = (o) => {
    setDialogOpen(o);
    if (!o) {
      // Dialog chiuso dopo apertura dal prompt → segna come gestito, così non
      // ridisturbiamo nelle sessioni successive.
      try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
      setVisible(false);
    }
  };

  if (!visible) {
    return <AddToDeviceDialog open={dialogOpen} onOpenChange={onDialogChange} clientName={clientName} />;
  }

  return (
    <>
      <div
        className="pointer-events-auto fixed inset-x-3 bottom-3 z-[110] rounded-2xl border border-border/70 bg-popover/95 p-3 shadow-2xl backdrop-blur-lg animate-in slide-in-from-bottom-6 sm:left-auto sm:right-4 sm:max-w-sm"
        data-testid="portal-a2h-prompt"
        role="dialog"
        aria-label={`Aggiungi ${platformName} alla schermata Home`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-foreground text-background">
            <Smartphone className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">
              Salva {platformName} sulla Home
            </div>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              {clientName
                ? `Apri il portale di ${clientName} con un tap. Nessuna app da scaricare.`
                : "Apri il portale con un tap. Nessuna app da scaricare."}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={openDialog}
                data-testid="portal-a2h-add"
                className="rounded-lg bg-foreground px-3 py-1.5 text-[12px] font-semibold text-background transition-transform hover:scale-[1.02]"
              >
                Aggiungi
              </button>
              <button
                onClick={dismiss}
                data-testid="portal-a2h-later"
                className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="portal-a2h-dismiss"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Chiudi"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <AddToDeviceDialog open={dialogOpen} onOpenChange={onDialogChange} clientName={clientName} />
    </>
  );
}
